// Фильтры документов
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  SwapHoriz as SwapHorizIcon,
  Description as DescriptionIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  DocumentType,
  DocumentStatus,
  DocumentDirection,
  SortBy,
  SortOrder,
  DocumentsStats
} from './types';
import { isErrorStatus, isInProgressStatus } from './utils';
import WarningAlertPanel from './WarningAlertPanel';
import BlockedNomenclatureDrawer from './BlockedNomenclatureDrawer';

interface DocumentFiltersProps {
  username?: string;
  pharmacyGuid?: string;
  filterType: DocumentType;
  filterStatus: DocumentStatus;
  filterDirection: DocumentDirection;
  sortBy: SortBy;
  sortOrder: SortOrder;
  stats: DocumentsStats;
  onFilterTypeChange: (type: DocumentType) => void;
  onFilterStatusChange: (status: DocumentStatus) => void;
  onFilterDirectionChange: (direction: DocumentDirection) => void;
  onSortByChange: (sortBy: SortBy) => void;
  onSortOrderChange: (order: SortOrder) => void;
  onResetFilters: () => void;
  onRefresh: () => void;
  onCreateDocument: () => void;
  canCreateDocument: boolean;
  isLoading: boolean;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

const DocumentFilters: React.FC<DocumentFiltersProps> = ({
  username,
  pharmacyGuid,
  filterType,
  filterStatus,
  filterDirection,
  sortBy,
  sortOrder,
  stats,
  onFilterTypeChange,
  onFilterStatusChange,
  onFilterDirectionChange,
  onSortByChange,
  onSortOrderChange,
  onResetFilters,
  onRefresh,
  onCreateDocument,
  canCreateDocument,
  isLoading,
  searchQuery = '',
  onSearchChange
}) => {
  const theme = useTheme();

  const [localSearch, setLocalSearch] = useState(searchQuery || '');

  useEffect(() => {
    setLocalSearch(searchQuery || '');
  }, [searchQuery]);

  const handleSearchChange = (val: string) => {
    setLocalSearch(val);
    onSearchChange?.(val);
  };

  const currentSearchValue = onSearchChange ? searchQuery : localSearch;

  return (
    <Box sx={{ mb: 3 }}>
      {/* 1. Dashboard Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Черновики */}
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Черновики"
            value={stats.drafts}
            color="warning"
            active={filterStatus === 9}
            onClick={() => onFilterStatusChange(filterStatus === 9 ? 'all' : 9)}
          />
        </Grid>
        {/* В работе */}
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="В работе"
            value={stats.inProgress}
            color="info"
            active={filterStatus === 'in_progress' || isInProgressStatus(typeof filterStatus === 'number' ? filterStatus : 0)}
            onClick={() => onFilterStatusChange(filterStatus === 'in_progress' ? 'all' : 'in_progress')}
          />
        </Grid>
        {/* Входящие (Waiting) */}
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Входящие"
            value={stats.incoming}
            color="success"
            active={filterDirection === 'incoming'}
            onClick={() => onFilterDirectionChange(filterDirection === 'incoming' ? 'all' : 'incoming')}
            icon={<SwapHorizIcon fontSize="small" />}
          />
        </Grid>
        {/* Ошибки / Отклонено (Combined or priority) */}
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Ошибки"
            value={stats.totalErrors}
            color="error"
            active={filterStatus === 'error' || isErrorStatus(typeof filterStatus === 'number' ? filterStatus : 0)}
            onClick={() => onFilterStatusChange(filterStatus === 'error' ? 'all' : 'error')}
          />
        </Grid>
        {/* Перемещения */}
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Перемещения"
            value={stats.transfers}
            color="secondary"
            active={filterType === 5}
            onClick={() => onFilterTypeChange(filterType === 5 ? 'all' : 5)}
            icon={<SwapHorizIcon fontSize="small" />}
          />
        </Grid>
        {/* Возвраты */}
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Возвраты"
            value={stats.returns}
            color="primary"
            active={filterType === 4}
            onClick={() => onFilterTypeChange(filterType === 4 ? 'all' : 4)}
            icon={<AssignmentIcon fontSize="small" />}
          />
        </Grid>
      </Grid>

      {/* 2. Filters Toolbar */}
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          bgcolor: 'background.paper',
          borderRadius: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>Фильтр:</Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Тип документа</InputLabel>
          <Select
            value={filterType}
            label="Тип документа"
            onChange={(e) => onFilterTypeChange(e.target.value as DocumentType)}
          >
            <MenuItem value="all">Все типы</MenuItem>
            <MenuItem value={4}>Возврат</MenuItem>
            <MenuItem value={5}>Перемещение</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={filterStatus}
            label="Статус"
            onChange={(e) => onFilterStatusChange(e.target.value as DocumentStatus)}
          >
            <MenuItem value="all">Все статусы</MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem value={9}>Черновик</MenuItem>
            <MenuItem value={1}>Новый</MenuItem>
            <MenuItem value={7}>В работе</MenuItem>
            <MenuItem value={15}>Ожидает подтверждения</MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem value={2}>Передан</MenuItem>
            <MenuItem value={6}>В пути</MenuItem>
            <MenuItem value={3}>Принят</MenuItem>
            <MenuItem value={4}>Принят частично</MenuItem>
            <MenuItem value={10}>Принят с расхождениями</MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem value={5} sx={{ color: 'error.main' }}>Отклонен</MenuItem>
            <MenuItem value={11} sx={{ color: 'error.main' }}>Ошибка остатка</MenuItem>
          </Select>
        </FormControl>

        <TextField
          id="documents-search"
          size="small"
          value={currentSearchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Поиск документа"
          sx={{ width: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: currentSearchValue ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearchChange('')} sx={{ p: 0.25 }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <Box sx={{ flexGrow: 1 }} /> {/* Spacer */}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <SortIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>Сортировка:</Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value as SortBy)}
            displayEmpty
            inputProps={{ 'aria-label': 'Сортировать по' }}
          >
            <MenuItem value="id">По номеру  </MenuItem>
            <MenuItem value="date">По дате</MenuItem>
            <MenuItem value="type">По типу</MenuItem>
            <MenuItem value="status">По статусу</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={sortOrder}
            onChange={(e) => onSortOrderChange(e.target.value as SortOrder)}
            displayEmpty
            inputProps={{ 'aria-label': 'Порядок' }}
          >
            <MenuItem value="desc">Сначала новые</MenuItem>
            <MenuItem value="asc">Сначала старые</MenuItem>
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title="Сбросить фильтры">
          <IconButton onClick={() => { handleSearchChange(''); onResetFilters(); }} size="small">
            <ClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Обновить">
          <span>
            <IconButton
              onClick={onRefresh}
              disabled={isLoading}
              size="small"
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <WarningAlertPanel username={username} />

        <BlockedNomenclatureDrawer pharmacyGuid={pharmacyGuid} />

        {canCreateDocument && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={onCreateDocument}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{ ml: 1, px: 3, fontWeight: 'bold' }}
          >
            Создать
          </Button>
        )}
      </Paper>
    </Box>
  );
};

// --- Subcomponents ---

interface StatCardProps {
  label: string;
  value: number;
  color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  active?: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, active, onClick, icon }) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: active ? alpha(theme.palette[color].main, 0.1) : 'white',
        border: '1px solid',
        borderColor: active ? theme.palette[color].main : 'divider',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'hidden',
        height: 100,
        '&:hover': {
          borderColor: theme.palette[color].main,
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[2]
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, position: 'relative', zIndex: 1 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h4" fontWeight={800} color={value > 0 ? 'text.primary' : 'text.disabled'} sx={{ mt: 1 }}>
            {value}
          </Typography>
        </Box>
      </CardContent>

      {/* Background Icon */}
      <Box sx={{
        position: 'absolute',
        right: -20,
        bottom: -20,
        color: theme.palette[color].main,
        opacity: active ? 0.2 : 0.1,
        transform: 'rotate(-20deg)',
        zIndex: 0,
        pointerEvents: 'none',
        '& svg': {
          fontSize: '120px'
        }
      }}>
        {icon || <DescriptionIcon />}
      </Box>
    </Card>
  );
};

export default DocumentFilters;

